import { useMemo, useState } from 'react';
import type { Issue } from '../../types';
import { STATUS_LABELS, STATUS_ORDER, type IssueSize } from '../../types';
import { displayName, initials, type Profiles } from '../../people';
import VendorTag from '../VendorTag';
import { epicBackground } from '../../epics';
import { blockerOf } from '../../blocked';
import {
  rollUpEpic,
  rollUpWorkspaceEpics,
  latestUpdateAcross,
  type BoardTier,
  type EpicHealth,
} from '../../epic-rollup';
import { LIFECYCLE_LABELS, type LifecycleTier } from '../../lifecycle';
import { pickDefaultPivot } from '../../pivot-default';
import { MONTHS } from '../../when';
import { steadyNow } from '../../steady-now';
import { IssueTextInline } from '../IssueText';

/**
 * EPIC DETAIL, ACROSS THE WORKSPACE — the Portfolio's By-epic drill (screen 03).
 * The sibling of the board-level epic detail, but an epic spans boards, so this
 * one rolls up across EVERY board the epic touches: the header figures, the latest
 * update and the scope are read from all its cards together, and the cards re-pivot
 * By board / By status / By person — the three cuts that stay meaningful across
 * boards (area and phase are each a board's own, so they live on the board).
 *
 * DERIVED, and what is NOT drawn. Owner, due, size-weighted progress, scope,
 * needs-attention, health and the latest update are all read from the member cards
 * (epic-rollup.ts). The Class badge (#435), the health-movement read (needs epic
 * pulse history), the throughput forecast (Monte-Carlo-at-epic) and a cross-board
 * budget roll-up are NOT in the data — they are left OUT, not stubbed: the screen
 * is what it honestly is today, and each lands as a real column when its feature
 * does. A card opens in the Portfolio's own lean panel (onOpenCard), never leaves.
 */

const HEALTH_WORD: Record<EpicHealth, string> = { ontrack: 'On track', atrisk: 'At risk', offtrack: 'Off track' };
const HEALTH_CLS: Record<EpicHealth, string> = { ontrack: 'g', atrisk: 'a', offtrack: 'r' };
const SIZE_LETTER: Record<IssueSize, string> = { quick: 'S', medium: 'M', long: 'L' };

type Pivot = 'board' | 'status' | 'person';
const PIVOT_LABEL: Record<Pivot, string> = { board: 'By board', status: 'By status', person: 'By person' };
const PIVOT_SUB: Record<Pivot, string> = {
  board: 'the board they live on — each board keeps its own phases and lifecycle tier',
  status: 'where they are on their board',
  person: 'who is carrying them, across the epic',
};

/** An ISO 'YYYY-MM-DD' to "15 Dec", read off the parts so no timezone shifts it. */
function fmtDue(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]}`;
}
function fmtStamp(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
function daysUntil(iso: string, now: number): number {
  const [y, m, d] = iso.split('-').map(Number);
  const due = Date.UTC(y, (m ?? 1) - 1, d ?? 1);
  const t = new Date(now);
  const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  return Math.round((due - today) / 86_400_000);
}

interface Props {
  name: string;
  cardsByBoard: Map<string, Issue[]>;
  boardTiers: BoardTier[];
  profiles: Profiles;
  onBack: () => void;
  onOpenCard: (projectId: string, cardId: string) => void;
}

export default function EpicDetail({ name, cardsByBoard, boardTiers, profiles, onBack, onOpenCard }: Props) {
  // HELD STILL WITHIN THE MINUTE, so the memos below that take `now` as a
  // dependency actually cache. `Date.now()` here was new on every render, so
  // every roll-up over every card re-ran every time. See src/steady-now.ts.
  const now = steadyNow();

  // Board lookups — name and lifecycle tier by id, for the SPANS line and the
  // By-board bands. Every board the Portfolio loaded is listed.
  const tierById = useMemo(() => new Map(boardTiers.map((b) => [b.id, b])), [boardTiers]);

  // Every member card across every board, each kept with the board it lives on so
  // a click can open it in place on the right board.
  const members = useMemo(() => {
    const out: { card: Issue; boardId: string }[] = [];
    for (const [boardId, cards] of cardsByBoard) {
      for (const c of cards) if ((c.epic ?? '').trim() === name) out.push({ card: c, boardId });
    }
    return out;
  }, [cardsByBoard, name]);
  const cards = useMemo(() => members.map((m) => m.card), [members]);
  const boardOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const { card, boardId } of members) m.set(card.id, boardId);
    return m;
  }, [members]);

  // Open on the most-populated cut (see src/pivot-default.ts), computed once on
  // mount; ties fall to status, then board, then person. The drill is keyed by
  // epic name in the Portfolio shell, so each epic mounts fresh with its own
  // default rather than inheriting the last epic's pivot.
  const [pivot, setPivot] = useState<Pivot>(() =>
    pickDefaultPivot<Pivot>(
      {
        status: new Set(cards.map((c) => c.status)).size,
        board: new Set(members.map((m) => m.boardId)).size,
        person: new Set(cards.map((c) => c.assignee).filter(Boolean)).size + (cards.some((c) => !c.assignee) ? 1 : 0),
      },
      ['status', 'board', 'person'],
    ),
  );

  const allNames = useMemo(() => {
    const s = new Set<string>();
    for (const cards of cardsByBoard.values()) for (const c of cards) {
      const e = (c.epic ?? '').trim();
      if (e) s.add(e);
    }
    return [...s];
  }, [cardsByBoard]);

  const roll = useMemo(() => rollUpEpic(name, cards, allNames, now), [name, cards, allNames, now]);
  const latest = useMemo(() => latestUpdateAcross(cards), [cards]);

  // The boards this epic spans, most cards first, with each board's lifecycle tier
  // — read from the same workspace roll-up the lens uses, so the two never disagree.
  const spans = useMemo(() => {
    const ws = rollUpWorkspaceEpics(cardsByBoard, boardTiers, now);
    return ws.epics.find((e) => e.name === name)?.boards ?? [];
  }, [cardsByBoard, boardTiers, name, now]);

  const cardSort = (a: Issue, b: Issue) => a.ref.localeCompare(b.ref, undefined, { numeric: true });

  type Band = { key: string; label: string; tier?: LifecycleTier; cards: Issue[] };
  const bands: Band[] = useMemo(() => {
    if (pivot === 'status') {
      return STATUS_ORDER.map((s) => ({ key: s, label: STATUS_LABELS[s], cards: cards.filter((c) => c.status === s) }))
        .filter((b) => b.cards.length > 0);
    }
    if (pivot === 'person') {
      const open = (c: Issue) => c.status !== 'done' && c.status !== 'wontdo';
      const who = [...new Set(cards.map((c) => c.assignee).filter(Boolean))];
      const out: Band[] = who
        .map((a) => ({ key: a, label: displayName(a, profiles), cards: cards.filter((c) => c.assignee === a) }))
        .sort((x, y) => {
          const d = y.cards.filter(open).length - x.cards.filter(open).length;
          return d !== 0 ? d : x.label.localeCompare(y.label);
        });
      const none = cards.filter((c) => !c.assignee);
      if (none.length) out.push({ key: '∅', label: 'No owner', cards: none });
      return out;
    }
    // By board — the spanning boards in the roll-up's order (most cards first).
    return spans.map((b) => ({
      key: b.id,
      label: b.name,
      tier: tierById.get(b.id)?.tier,
      cards: cards.filter((c) => boardOf.get(c.id) === b.id),
    }));
  }, [pivot, cards, profiles, spans, tierById, boardOf]);

  const renderCardRow = (c: Issue) => {
    const blocked = blockerOf(c);
    const resolved = c.status === 'done' || c.status === 'wontdo';
    let mark: string;
    let markWarn = false;
    if (resolved) {
      mark = STATUS_LABELS[c.status].toLowerCase();
    } else if (c.dueDate) {
      const d = daysUntil(c.dueDate, now);
      mark = d === 0 ? 'today' : d > 0 ? `${d}d` : `${-d}d over`;
      markWarn = d <= 0;
    } else {
      mark = STATUS_LABELS[c.status].toLowerCase();
    }
    return (
      <button type="button" key={c.id} className="epd-card" onClick={() => onOpenCard(boardOf.get(c.id)!, c.id)}>
        <span className="epd-ref">{c.ref}</span>
        {c.size ? <span className="epd-sz">{SIZE_LETTER[c.size]}</span> : <span className="epd-sz epd-sz-none" aria-hidden="true" />}
        <span className="epd-title">
          {c.title}
          {blocked ? <span className="epd-blocked"> · blocked: <IssueTextInline text={blocked} /></span> : null}
        </span>
        <span className="epd-cardmark">
          <span className="epd-dot" style={{ background: `var(--s-${c.status})` }} aria-hidden="true" />
          <span className={markWarn ? 'epd-mark-warn' : undefined}>{mark}</span>
        </span>
      </button>
    );
  };

  const renderBand = (b: Band) => {
    const br = rollUpEpic(b.label, b.cards, [], now);
    const blockedN = b.cards.filter((c) => blockerOf(c) !== '' && c.status !== 'done' && c.status !== 'wontdo').length;
    const openN = b.cards.filter((c) => c.status !== 'done' && c.status !== 'wontdo').length;
    return (
      <div className="epd-band" key={b.key}>
        <div className="epd-band-head">
          <div className="epd-band-name">
            {pivot === 'board' && <span className="epd-band-sw" style={{ background: epicBackground(b.label, spans.map((s) => s.name)) }} aria-hidden="true" />}
            {b.label}
            {pivot === 'person' && <VendorTag who={b.key} profiles={profiles} compact />}
            {' '}<span className="epd-band-n">{b.cards.length}</span>
            {b.tier && <span className="epd-tier">{LIFECYCLE_LABELS[b.tier]}</span>}
            {blockedN > 0 && <span className="epd-band-blocked">{blockedN} blocked</span>}
          </div>
          <div className="epd-band-right">
            <div className="pf-pct epd-band-pct">
              <span className="fig">{br.progressPct}%</span>
              <div className="bar"><div className="fill" style={{ width: `${br.progressPct}%` }} /></div>
            </div>
            {pivot === 'person' ? (
              <span className="epd-band-owner">{openN} open · {blockedN} blocked</span>
            ) : (
              <span className="epd-band-owner">{br.owner ? displayName(br.owner, profiles) : '—'}</span>
            )}
          </div>
        </div>
        <div className="epd-band-cards">{[...b.cards].sort(cardSort).map(renderCardRow)}</div>
      </div>
    );
  };

  const boardWord = spans.length === 1 ? 'board' : 'boards';

  return (
    <>
      <div className="epics-top">
        <div className="epics-crumb">
          <button type="button" className="link" onClick={onBack}>Portfolio</button>
          <span className="epics-crumb-sep" aria-hidden="true">›</span>
          <button type="button" className="link" onClick={onBack}>By epic</button>
          <span className="epics-crumb-sep" aria-hidden="true">›</span>
          <span className="epics-crumb-cur">{name}</span>
        </div>
      </div>

      <div className="epd-title-row">
        <div className="epd-title-main">
          <span className="epic-sw epd-title-sw" style={{ background: epicBackground(name, allNames) }} aria-hidden="true" />
          <h1 className="epd-h1">{name}</h1>
        </div>
        <span className={`rag ${HEALTH_CLS[roll.health]}`}><span className="dot" />{HEALTH_WORD[roll.health]}</span>
      </div>
      <p className="epd-subline">
        {cards.length} {cards.length === 1 ? 'card' : 'cards'} across {spans.length} {boardWord}
      </p>

      {/* SPANS — the boards the epic touches, each with its own lifecycle tier. */}
      {spans.length > 0 && (
        <div className="epd-spans">
          <span className="epd-spans-k">Spans</span>
          {spans.map((b) => {
            const tier = tierById.get(b.id)?.tier;
            return (
              <span className="epd-span" key={b.id}>
                <span className="epd-span-sw" style={{ background: epicBackground(b.name, spans.map((s) => s.name)) }} aria-hidden="true" />
                {b.name}
                {tier && <span className="epd-tier">{LIFECYCLE_LABELS[tier]}</span>}
              </span>
            );
          })}
        </div>
      )}

      {latest ? (
        <div className="epd-latest">
          <div className="epd-latest-body">
            <div className="epd-latest-head">Latest update · {fmtStamp(latest.at)} · derived from {latest.ref}</div>
            <div className="epd-latest-text"><IssueTextInline text={latest.text} /></div>
            <div className="epd-latest-by">
              {latest.by ? displayName(latest.by, profiles) : 'FlightDeck'}
              {latest.nextStep ? ` · next: ${latest.nextStep}` : ''}
            </div>
          </div>
          <button type="button" className="link epd-latest-open" onClick={() => onOpenCard(boardOf.get(latest.issueId)!, latest.issueId)}>
            Open {latest.ref} →
          </button>
        </div>
      ) : (
        <div className="epd-latest epd-latest-empty">No updates yet — post one on a card and the newest shows here.</div>
      )}

      <div className="epd-cells">
        <div className="epd-cell">
          <div className="epd-cell-k">Owner <span className="epd-auto">auto</span></div>
          <div className="epd-cell-owner">
            {roll.owner ? (
              <>
                <span className="comment-av">{initials(roll.owner, profiles)}</span>
                <span className="epd-cell-v">{displayName(roll.owner, profiles)}<VendorTag who={roll.owner} profiles={profiles} compact /></span>
              </>
            ) : (
              <span className="epd-cell-v" style={{ color: 'var(--t3)' }}>—</span>
            )}
          </div>
          <div className="epd-cell-sub">most cards’ owner</div>
        </div>

        <div className="epd-cell">
          <div className="epd-cell-k">Due <span className="epd-auto">auto</span></div>
          {roll.dueAuto ? (
            <>
              <div className={`epd-cell-fig${roll.dueState === 'overdue' ? ' epd-mark-warn' : ''}`}>{fmtDue(roll.dueAuto)}</div>
              <div className="epd-cell-sub">latest card’s date</div>
            </>
          ) : (
            <>
              <div className="epd-cell-fig" style={{ color: 'var(--t3)' }}>—</div>
              <div className="epd-cell-sub">no dated card</div>
            </>
          )}
        </div>

        <div className="epd-cell">
          <div className="epd-cell-k">Progress</div>
          <div className="epd-cell-fig">{roll.progressPct}<span className="epd-pct-sign">%</span></div>
          <div className="pf-pct epd-cell-bar"><div className="bar"><div className="fill" style={{ width: `${roll.progressPct}%` }} /></div></div>
          <div className="epd-cell-sub">{roll.doneCount} of {cards.length} done · all boards</div>
        </div>

        <div className="epd-cell">
          <div className="epd-cell-k">Scope</div>
          <div className="epd-cell-fig">{roll.scopeAdded > 0 ? `+${roll.scopeAdded}` : '—'}</div>
          <div className="epd-cell-sub">
            {roll.scopeAdded > 0 && roll.startedMs ? `cards added since ${fmtStamp(roll.startedMs)}` : 'no cards added since it began'}
          </div>
        </div>

        <div className="epd-cell">
          <div className="epd-cell-k">Needs attention</div>
          <div className={`epd-cell-fig${roll.needsAttention > 0 ? ' epd-mark-warn' : ''}`}>{roll.needsAttention}</div>
          <div className="epd-cell-sub">blocked or past due</div>
        </div>
      </div>

      <div className="epd-pivot">
        <div className="seg epd-pivot-seg" role="group" aria-label="Group the cards by">
          {(['board', 'status', 'person'] as Pivot[]).map((p) => (
            <button
              key={p}
              type="button"
              className={`seg-btn ${pivot === p ? 'active' : ''}`}
              aria-pressed={pivot === p}
              onClick={() => setPivot(p)}
            >
              {PIVOT_LABEL[p]}
            </button>
          ))}
        </div>
        <span className="epd-pivot-sub">{cards.length} {cards.length === 1 ? 'card' : 'cards'} grouped by {PIVOT_SUB[pivot]}</span>
      </div>

      <div className="epd-bands">
        {bands.length === 0 ? (
          <div className="pf-owner" style={{ color: 'var(--t3)', padding: '12px 2px' }}>No cards on this epic yet.</div>
        ) : (
          bands.map(renderBand)
        )}
      </div>

      <p className="hint epics-foot">
        <b>Scope and delivery, across every board.</b> Owner, health, progress, scope and the due date are all read
        from the member cards; each board still owns its own phases, budget and lifecycle tier. Post updates on a card.
      </p>
    </>
  );
}
